import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const LanguageContext = createContext();

const translations = {
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      gallery: 'Gallery',
      packages: 'Packages',
      contact: 'Contact',
      registerNow: 'Register Now',
      destinations: 'Destinations'
    },
    hero: {
      title: 'ASEAN Business Forum',
      subtitle: 'A Strategic Platform for Trade, Investment & the Halal Economy',
      location: 'Batam & Bintan, Indonesia',
      ctaPrimary: 'Become an Exhibitor',
      ctaSecondary: 'Download Brochure',
      year: '2026',
      feature: 'B2B Matchmaking'
    },
    countdown: {
      title: 'Event Countdown',
      subtitle: 'Forum Begins',
      days: 'Days',
      hours: 'Hours',
      minutes: 'Minutes',
      seconds: 'Seconds'
    },
    countdownFeatures: {
      b2b: 'B2B Matchmaking',
      b2bDesc: 'Curated meetings with qualified buyers, traders & distributors.',
      marketplace: 'Exhibition & Trade',
      marketplaceDesc: 'Dedicated exhibition space across priority halal sectors.',
      delegations: 'Government & Delegations',
      delegationsDesc: 'B2G, G2G & global bridging with official delegations.',
      networking: 'Investment & Networking',
      networkingDesc: 'Key investors meet & exclusive strategic partnership facilitation.'
    },
    stats: {
      exhibitors: 'Exhibitors',
      countries: 'Countries',
      delegations: 'Delegations',
      attendees: 'Attendees'
    },
    why: {
      title: 'Why Attend the ASEAN Business Forum?',
      description: 'The global halal economy has evolved beyond consumption into a strategic engine for trade, investment, and industrial development. As ASEAN and OIC markets deepen economic cooperation, this forum provides a credible, government-aligned platform for real economic execution.',
      marketGrowth: 'Trillion-Dollar Opportunity',
      marketGrowthDesc: 'Access the rapidly expanding halal economy entering its execution phase.',
      globalNetwork: 'Cross-Border Connectivity',
      globalNetworkDesc: 'Connect with stakeholders across ASEAN, OIC, GCC, EU and Asia markets.',
      innovationHub: 'Free Trade Zone Advantage',
      innovationHubDesc: 'Leverage dual Free Trade Zone & Special Economic Zone benefits in Batam-Bintan.',
      credibility: 'Execution-Driven Platform',
      credibilityDesc: 'Outcome-driven forum focused on trade facilitation and investment activation.'
    },
    about: {
      title: 'About the Forum',
      connect: 'Connect',
      connectDesc: 'A high-level platform connecting governments, investors, institutions, and industry leaders to accelerate trade and cross-border collaboration within the global halal economy.',
      showcase: 'Collaborate',
      showcaseDesc: 'Facilitate strategic partnerships, MoU signing, and public-private cooperation across digital infrastructure, logistics, manufacturing, tourism, and halal industries.',
      scale: 'Grow',
      scaleDesc: 'Enable long-term deal flow, investment continuity, and industry scale-up across ASEAN-OIC-Global markets with Free Trade Zone-based opportunities.'
    },
    destinations: {
      title: 'Host Destinations: Batam & Bintan, INDONESIA (FREE TRADE ZONES)',
      batam: 'BATAM',
      batamDesc: 'Singapore-connected trade and digital gateway. Mature industrial, logistics, and port ecosystem. Established data center and digital infrastructure environment including Nongsa Digital Park with US$410M+ in investments.',
      bintan: 'BINTAN',
      bintanDesc: 'Cost-efficient base for investment and manufacturing. Strategic location near Singapore. Strong potential in halal industry, tourism, and wellness. Built for long-term sustainable investment.'
    },
    packages: {
      title: 'Participation Packages',
      subtitle: 'Choose the right engagement level for your organization',
      delegate: 'Delegate',
      delegateDesc: 'For government leaders, investors, and industry professionals seeking strategic connections.',
      booth: 'Exhibitor',
      boothDesc: 'For companies, regions, and institutions to present solutions and engage with buyers.',
      sponsorship: 'Strategic Partner',
      sponsorshipDesc: 'Maximum visibility and branding for organizations shaping the halal economy.',
      contactUs: 'Contact Us',
      learnMore: 'Learn More',
      popular: 'Popular',
      features: {
        fullAccess: 'Full forum access',
        networkingSessions: 'Networking sessions',
        conferenceMaterials: 'Conference materials',
        certificate: 'Certificate of participation',
        premiumSpace: 'Premium exhibition space',
        b2bMatchmaking: 'B2B matchmaking sessions',
        marketingInclusion: 'Marketing inclusion',
        leadGeneration: 'Lead generation tools',
        premiumBranding: 'Premium branding placement',
        speakingOpportunities: 'Speaking opportunities',
        vipAccess: 'VIP networking access',
        mediaCoverage: 'Media coverage'
      }
    },
    gallery: {
      title: 'Our Success Stories',
      description: 'Building on the success of the Asian Business Forum 2025 in Batam',
      photos: 'Photos',
      videos: 'Videos',
      viewAll: 'View All',
      noVideos: 'No videos available yet',
      videosComingSoon: 'Videos will be added soon'
    },
    success: {
      title: 'Key Outcomes',
      story1Title: 'Trade Facilitation',
      story1Metric: 'MoU Signing',
      story1Quote: 'Strategic partnership and MoU facilitation, supporting collaboration beyond the forum with trade and investment pipelines across priority sectors.',
      story1Author: 'Asian Business Forum 2025',
      story2Title: 'Women Empowerment',
      story2Metric: '300+ Entrepreneurs',
      story2Quote: 'Empowering women entrepreneurs across 10+ countries through inspiring keynotes, business matching, pitch showcases, and meaningful connections.',
      story2Author: 'Women Entrepreneurs Summit',
      story3Title: 'Government Partnership',
      story3Metric: 'Public-Private',
      story3Quote: 'Cross-border and inter-regional business connections, policy alignment, and increased visibility for participating regions and institutions.',
      story3Author: 'ASEAN-OIC Collaboration'
    },
    brochure: {
      title: 'Official Forum Brochure',
      description: 'Download the complete ASEAN Business Forum 2026 brochure for detailed agenda, speaker line-up, venue information, and partnership opportunities.',
      download: 'Download Brochure (PDF)',
      requestDeck: 'Request Partnership Deck',
      note: 'Includes exhibitor packages, floor plans, and strategic partnership tiers.',
      comingSoon: 'Brochure will be available soon'
    },
    exhibitor: {
      title: 'Become an Exhibitor',
      benefitsTitle: 'Exhibitor Benefits',
      globalVisibility: 'Strategic Exposure',
      globalVisibilityDesc: 'Prominent branding within a curated, high-level audience of decision-makers.',
      curatedMeetings: 'Curated B2B Meetings',
      curatedMeetingsDesc: 'Pre-scheduled meetings with qualified buyers, traders & distributors.',
      accessDelegations: 'Government Access',
      accessDelegationsDesc: 'Direct interaction with official country delegations and policy makers.',
      marketingInclusion: 'Investment Exposure',
      marketingInclusionDesc: 'Site visits in Bintan highlighting Free Trade Zone opportunities.',
      networkingEvents: 'Networking Events',
      networkingEventsDesc: 'Exclusive dinners, roundtables, and key investors meet sessions.'
    },
    form: {
      title: 'Request Information',
      fullName: 'Full Name',
      company: 'Company / Organization',
      country: 'Country',
      email: 'Email',
      whatsapp: 'WhatsApp',
      category: 'Industry / Sector',
      selectCategory: 'Select sector',
      interests: 'I am interested in',
      delegate: 'Delegate (Visitor)',
      booth: 'Exhibitor / Booth',
      sponsorship: 'Strategic Partnership',
      message: 'Message',
      submit: 'Submit Enquiry',
      submitting: 'Submitting...',
      successMessage: 'Thank you! Your enquiry has been submitted. Our team will contact you within 48 hours.',
      errorMessage: 'Failed to submit form. Please try again.',
      selectInterest: 'Please select at least one interest'
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'Get in touch with our team',
      whatsapp: 'WhatsApp',
      email: 'Email',
      location: 'Location',
      sendMessage: 'Send message',
      sendEmail: 'Send email',
      viewDestinations: 'View destinations',
      formTitle: 'Send a Message',
      name: 'Name',
      messagePlaceholder: 'Your message...',
      sendButton: 'Send Message',
      sending: 'Sending...',
      successMessage: 'Thank you for your message. We will respond soon.',
      errorMessage: 'Failed to submit message. Please try again.'
    },
    footer: {
      quickLinks: 'Quick Links',
      followUs: 'Follow Us',
      copyright: '\u00a9 2026 ASEAN Business Forum. All rights reserved.',
      privacyPolicy: 'Privacy Policy',
      termsOfService: 'Terms of Service',
      tagline: 'ASEAN Business Forum 2026 - Batam & Bintan, Indonesia'
    },
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      add: 'Add',
      required: 'Required',
      close: 'Close',
      viewMore: 'View More',
      readMore: 'Read More'
    },
    categories: {
      foodBeverage: 'Food & Beverage',
      cosmetics: 'Cosmetics & Personal Care',
      pharma: 'Pharmaceuticals & Healthcare',
      fashion: 'Fashion & Modest Wear',
      finance: 'Islamic Finance & Investment',
      tourism: 'Tourism, Wellness & Hospitality',
      technology: 'Digital Economy & Fintech',
      logistics: 'Logistics & Supply Chain',
      education: 'Education & Training',
      media: 'Media & Entertainment',
      agriculture: 'Agriculture & Farming',
      manufacturing: 'Halal Manufacturing & Trade',
      retail: 'Retail & E-commerce',
      certification: 'Halal Certification Bodies',
      government: 'Government & Trade Agencies',
      investment: 'Investment & Venture Capital',
      others: 'Others'
    }
  },
  tr: {
    nav: {
      home: 'Ana Sayfa',
      about: 'Hakk\u0131nda',
      gallery: 'Galeri',
      packages: 'Paketler',
      contact: '\u0130leti\u015fim',
      registerNow: '\u015eimdi Kay\u0131t Ol',
      destinations: 'Destinasyonlar'
    },
    hero: {
      title: 'ASEAN Business Forum',
      subtitle: 'Ticaret, Yat\u0131r\u0131m ve Helal Ekonomisi i\u00e7in Stratejik Bir Platform',
      location: 'Batam ve Bintan, Endonezya',
      ctaPrimary: 'Kat\u0131l\u0131mc\u0131 Ol',
      ctaSecondary: 'Bro\u015f\u00fcr\u00fc \u0130ndir',
      year: '2026',
      feature: 'B2B E\u015fle\u015ftirme'
    },
    countdown: {
      title: 'Etkinlik Geri Say\u0131m\u0131',
      subtitle: 'Forum Ba\u015flang\u0131c\u0131',
      days: 'G\u00fcn',
      hours: 'Saat',
      minutes: 'Dakika',
      seconds: 'Saniye'
    },
    countdownFeatures: {
      b2b: 'B2B E\u015fle\u015ftirme',
      b2bDesc: 'Nitelikli al\u0131c\u0131lar, t\u00fcccarlar ve distrib\u00fct\u00f6rlerle \u00f6zel toplant\u0131lar.',
      marketplace: 'Sergi ve Ticaret',
      marketplaceDesc: '\u00d6ncelikli helal sekt\u00f6rlerinde \u00f6zel sergi alanlar\u0131.',
      delegations: 'H\u00fck\u00fcmet ve Delegasyonlar',
      delegationsDesc: 'Resmi delegasyonlarla B2G, G2G ve k\u00fcresel k\u00f6pr\u00fc kurma.',
      networking: 'Yat\u0131r\u0131m ve A\u011f Olu\u015fturma',
      networkingDesc: 'Kilit yat\u0131r\u0131mc\u0131 bulu\u015fmalar\u0131 ve stratejik ortakl\u0131k kolayla\u015ft\u0131rma.'
    },
    stats: {
      exhibitors: 'Kat\u0131l\u0131mc\u0131lar',
      countries: '\u00dclkeler',
      delegations: 'Delegasyonlar',
      attendees: 'Ziyaret\u00e7iler'
    },
    why: {
      title: 'ASEAN Business Forum\'a Neden Kat\u0131lmal\u0131s\u0131n\u0131z?',
      description: 'K\u00fcresel helal ekonomisi t\u00fcketimin \u00f6tesine ge\u00e7erek ticaret, yat\u0131r\u0131m ve end\u00fcstriyel kalk\u0131nma i\u00e7in stratejik bir motora d\u00f6n\u00fc\u015ft\u00fc. ASEAN ve \u0130\u0130T pazarlar\u0131 ekonomik i\u015fbirli\u011fini derinle\u015ftirirken, bu forum ger\u00e7ek ekonomik y\u00fcr\u00fctme i\u00e7in g\u00fcvenilir bir platform sunuyor.',
      marketGrowth: 'Trilyon Dolarl\u0131k F\u0131rsat',
      marketGrowthDesc: 'Y\u00fcr\u00fctme a\u015famas\u0131na giren h\u0131zla b\u00fcy\u00fcyen helal ekonomisine eri\u015fim.',
      globalNetwork: 'S\u0131n\u0131r \u00d6tesi Ba\u011flant\u0131',
      globalNetworkDesc: 'ASEAN, \u0130\u0130T, K\u00f6rfez, AB ve Asya pazarlar\u0131ndaki payda\u015flarla ba\u011flant\u0131.',
      innovationHub: 'Serbest Ticaret B\u00f6lgesi Avantaj\u0131',
      innovationHubDesc: 'Batam-Bintan\'daki ikili Serbest Ticaret B\u00f6lgesi ve \u00d6zel Ekonomi B\u00f6lgesi avantajlar\u0131.',
      credibility: 'Y\u00fcr\u00fctme Odakl\u0131 Platform',
      credibilityDesc: 'Ticaret kolayla\u015ft\u0131rma ve yat\u0131r\u0131m aktivasyonuna odaklanan sonu\u00e7 odakl\u0131 forum.'
    },
    about: {
      title: 'Forum Hakk\u0131nda',
      connect: 'Ba\u011flan',
      connectDesc: 'H\u00fck\u00fcmetleri, yat\u0131r\u0131mc\u0131lar\u0131, kurumlar\u0131 ve sekt\u00f6r liderlerini bir araya getirerek k\u00fcresel helal ekonomisinde ticareti h\u0131zland\u0131ran \u00fcst d\u00fczey bir platform.',
      showcase: '\u0130\u015fbirli\u011fi Yap',
      showcaseDesc: 'Dijital altyap\u0131, lojistik, \u00fcretim, turizm ve helal end\u00fcstrileri genelinde stratejik ortakl\u0131klar\u0131 ve kamu-\u00f6zel i\u015fbirli\u011fini kolayla\u015ft\u0131r\u0131n.',
      scale: 'B\u00fcy\u00fc',
      scaleDesc: 'Serbest Ticaret B\u00f6lgesi f\u0131rsatlar\u0131yla ASEAN-\u0130\u0130T-K\u00fcresel pazarlarda uzun vadeli anla\u015fma ak\u0131\u015f\u0131 ve sekt\u00f6r \u00f6l\u00e7e\u011fi b\u00fcy\u00fctmesi sa\u011flay\u0131n.'
    },
    destinations: {
      title: 'Ev Sahibi Destinasyonlar: Batam ve Bintan, ENDONEZYA (SERBEST T\u0130CARET B\u00d6LGELER\u0130)',
      batam: 'BATAM',
      batamDesc: 'Singapur ba\u011flant\u0131l\u0131 ticaret ve dijital ge\u00e7it. Olgun end\u00fcstriyel, lojistik ve liman ekosistemi. Nongsa Dijital Park\'ta 410 milyon dolar\u0131 a\u015fk\u0131n yat\u0131r\u0131mlarla kurulmu\u015f veri merkezi ve dijital altyap\u0131.',
      bintan: 'BINTAN',
      bintanDesc: 'Yat\u0131r\u0131m ve \u00fcretim i\u00e7in uygun maliyetli \u00fcs. Singapur\'a yak\u0131n stratejik konum. Helal end\u00fcstri, turizm ve sa\u011fl\u0131kta g\u00fc\u00e7l\u00fc potansiyel. Uzun vadeli s\u00fcrd\u00fcr\u00fclebilir yat\u0131r\u0131m i\u00e7in in\u015fa edilmi\u015f.'
    },
    packages: {
      title: 'Kat\u0131l\u0131m Paketleri',
      subtitle: 'Kurulu\u015funuz i\u00e7in do\u011fru kat\u0131l\u0131m seviyesini se\u00e7in',
      delegate: 'Delege',
      delegateDesc: 'Stratejik ba\u011flant\u0131lar arayan h\u00fck\u00fcmet liderleri, yat\u0131r\u0131mc\u0131lar ve sekt\u00f6r profesyonelleri i\u00e7in.',
      booth: 'Kat\u0131l\u0131mc\u0131',
      boothDesc: '\u00c7\u00f6z\u00fcmlerini sunmak ve al\u0131c\u0131larla etkile\u015fimde bulunmak isteyen \u015firketler ve kurumlar i\u00e7in.',
      sponsorship: 'Stratejik Ortak',
      sponsorshipDesc: 'Helal ekonomisini \u015fekillendiren kurulu\u015flar i\u00e7in maksimum g\u00f6r\u00fcn\u00fcrl\u00fck.',
      contactUs: 'Bize Ula\u015f\u0131n',
      learnMore: 'Daha Fazla Bilgi',
      popular: 'Pop\u00fcler',
      features: {
        fullAccess: 'Tam forum eri\u015fimi',
        networkingSessions: '\u0130\u015f a\u011f\u0131 oturumlar\u0131',
        conferenceMaterials: 'Konferans materyalleri',
        certificate: 'Kat\u0131l\u0131m sertifikas\u0131',
        premiumSpace: 'Premium sergi alan\u0131',
        b2bMatchmaking: 'B2B e\u015fle\u015ftirme oturumlar\u0131',
        marketingInclusion: 'Pazarlama dahil',
        leadGeneration: 'Potansiyel m\u00fc\u015fteri ara\u00e7lar\u0131',
        premiumBranding: 'Premium marka yerle\u015fimi',
        speakingOpportunities: 'Konu\u015fma f\u0131rsatlar\u0131',
        vipAccess: 'VIP a\u011f eri\u015fimi',
        mediaCoverage: 'Medya kapsam\u0131'
      }
    },
    gallery: {
      title: 'Ba\u015far\u0131 Hikayelerimiz',
      description: 'Batam\'daki Asian Business Forum 2025\'in ba\u015far\u0131s\u0131 \u00fczerine in\u015fa ediyoruz',
      photos: 'Foto\u011fraflar',
      videos: 'Videolar',
      viewAll: 'T\u00fcm\u00fcn\u00fc G\u00f6r',
      noVideos: 'Hen\u00fcz video bulunmuyor',
      videosComingSoon: 'Videolar yak\u0131nda eklenecek'
    },
    success: {
      title: 'Temel Sonu\u00e7lar',
      story1Title: 'Ticaret Kolayla\u015ft\u0131rma',
      story1Metric: 'MoU \u0130mzalama',
      story1Quote: 'Forumun \u00f6tesinde i\u015fbirli\u011fini destekleyen stratejik ortakl\u0131k ve mutabakat zapt\u0131 kolayla\u015ft\u0131rma.',
      story1Author: 'Asian Business Forum 2025',
      story2Title: 'Kad\u0131n G\u00fc\u00e7lendirme',
      story2Metric: '300+ Giri\u015fimci',
      story2Quote: '10\'dan fazla \u00fclkeden kad\u0131n giri\u015fimcileri ilham verici konu\u015fmalar, i\u015f e\u015fle\u015ftirme ve anlaml\u0131 ba\u011flant\u0131larla g\u00fc\u00e7lendirme.',
      story2Author: 'Kad\u0131n Giri\u015fimciler Zirvesi',
      story3Title: 'H\u00fck\u00fcmet Ortakl\u0131\u011f\u0131',
      story3Metric: 'Kamu-\u00d6zel',
      story3Quote: 'S\u0131n\u0131r \u00f6tesi i\u015f ba\u011flant\u0131lar\u0131, politika uyumu ve kat\u0131l\u0131mc\u0131 b\u00f6lgeler i\u00e7in artan g\u00f6r\u00fcn\u00fcrl\u00fck.',
      story3Author: 'ASEAN-\u0130\u0130T \u0130\u015fbirli\u011fi'
    },
    brochure: {
      title: 'Resmi Forum Bro\u015f\u00fcr\u00fc',
      description: 'Detayl\u0131 program, konu\u015fmac\u0131 listesi ve ortakl\u0131k f\u0131rsatlar\u0131 i\u00e7in ASEAN Business Forum 2026 bro\u015f\u00fcr\u00fcn\u00fc indirin.',
      download: 'Bro\u015f\u00fcr\u00fc \u0130ndir (PDF)',
      requestDeck: 'Ortakl\u0131k Dosyas\u0131n\u0131 \u0130ste',
      note: 'Kat\u0131l\u0131mc\u0131 paketleri, kat planlar\u0131 ve stratejik ortakl\u0131k seviyeleri dahildir.',
      comingSoon: 'Bro\u015f\u00fcr yak\u0131nda haz\u0131r olacak'
    },
    exhibitor: {
      title: 'Kat\u0131l\u0131mc\u0131 Olun',
      benefitsTitle: 'Kat\u0131l\u0131mc\u0131 Avantajlar\u0131',
      globalVisibility: 'Stratejik Tan\u0131t\u0131m',
      globalVisibilityDesc: 'Karar vericilerden olu\u015fan se\u00e7kin bir kitlede \u00f6ne \u00e7\u0131kan marka.',
      curatedMeetings: '\u00d6zel B2B Toplant\u0131lar\u0131',
      curatedMeetingsDesc: 'Nitelikli al\u0131c\u0131lar ve distrib\u00fct\u00f6rlerle \u00f6nceden planlanm\u0131\u015f toplant\u0131lar.',
      accessDelegations: 'H\u00fck\u00fcmet Eri\u015fimi',
      accessDelegationsDesc: 'Resmi \u00fclke delegasyonlar\u0131 ve politika yap\u0131c\u0131larla do\u011frudan etkile\u015fim.',
      marketingInclusion: 'Yat\u0131r\u0131m Tan\u0131t\u0131m\u0131',
      marketingInclusionDesc: 'Serbest Ticaret B\u00f6lgesi f\u0131rsatlar\u0131n\u0131 \u00f6ne \u00e7\u0131karan Bintan saha ziyaretleri.',
      networkingEvents: 'Networking Etkinlikleri',
      networkingEventsDesc: '\u00d6zel ak\u015fam yemekleri, yuvarlak masa toplant\u0131lar\u0131 ve kilit yat\u0131r\u0131mc\u0131 bulu\u015fmalar\u0131.'
    },
    form: {
      title: 'Bilgi Talep Et',
      fullName: 'Ad Soyad',
      company: '\u015eirket / Kurulu\u015f',
      country: '\u00dclke',
      email: 'E-posta',
      whatsapp: 'WhatsApp',
      category: 'Sekt\u00f6r',
      selectCategory: 'Sekt\u00f6r se\u00e7in',
      interests: '\u0130lgilendigim konular',
      delegate: 'Delege (Ziyaret\u00e7i)',
      booth: 'Kat\u0131l\u0131mc\u0131 / Stand',
      sponsorship: 'Stratejik Ortakl\u0131k',
      message: 'Mesaj',
      submit: 'Ba\u015fvuru G\u00f6nder',
      submitting: 'G\u00f6nderiliyor...',
      successMessage: 'Te\u015fekkurler! Ba\u015fvurunuz al\u0131nd\u0131. Ekibimiz 48 saat i\u00e7inde sizinle ileti\u015fime ge\u00e7ecektir.',
      errorMessage: 'Form g\u00f6nderilemedi. L\u00fctfen tekrar deneyin.',
      selectInterest: 'L\u00fctfen en az bir ilgi alan\u0131 se\u00e7in'
    },
    contact: {
      title: 'Bize Ula\u015f\u0131n',
      subtitle: 'Ekibimizle ileti\u015fime ge\u00e7in',
      whatsapp: 'WhatsApp',
      email: 'E-posta',
      location: 'Konum',
      sendMessage: 'Mesaj g\u00f6nder',
      sendEmail: 'E-posta g\u00f6nder',
      viewDestinations: 'Destinasyonlar\u0131 g\u00f6r',
      formTitle: 'Mesaj G\u00f6nderin',
      name: '\u0130sim',
      messagePlaceholder: 'Mesaj\u0131n\u0131z...',
      sendButton: 'Mesaj G\u00f6nder',
      sending: 'G\u00f6nderiliyor...',
      successMessage: 'Mesaj\u0131n\u0131z i\u00e7in te\u015fekkurler. En k\u0131sa s\u00fcrede yan\u0131t verece\u011fiz.',
      errorMessage: 'Mesaj g\u00f6nderilemedi. L\u00fctfen tekrar deneyin.'
    },
    footer: {
      quickLinks: 'H\u0131zl\u0131 Ba\u011flant\u0131lar',
      followUs: 'Bizi Takip Edin',
      copyright: '\u00a9 2026 ASEAN Business Forum. T\u00fcm haklar\u0131 sakl\u0131d\u0131r.',
      privacyPolicy: 'Gizlilik Politikas\u0131',
      termsOfService: 'Kullan\u0131m \u015eartlar\u0131',
      tagline: 'ASEAN Business Forum 2026 - Batam ve Bintan, Endonezya'
    },
    common: {
      loading: 'Y\u00fckleniyor...',
      error: 'Hata',
      success: 'Ba\u015far\u0131l\u0131',
      cancel: '\u0130ptal',
      save: 'Kaydet',
      delete: 'Sil',
      edit: 'D\u00fczenle',
      add: 'Ekle',
      required: 'Zorunlu',
      close: 'Kapat',
      viewMore: 'Daha Fazla',
      readMore: 'Devam\u0131n\u0131 Oku'
    },
    categories: {
      foodBeverage: 'G\u0131da ve \u0130\u00e7ecek',
      cosmetics: 'Kozmetik ve Ki\u015fisel Bak\u0131m',
      pharma: '\u0130la\u00e7 ve Sa\u011fl\u0131k',
      fashion: 'Moda ve Muhafazakar Giyim',
      finance: '\u0130slami Finans ve Yat\u0131r\u0131m',
      tourism: 'Turizm, Sa\u011fl\u0131k ve Konaklama',
      technology: 'Dijital Ekonomi ve Fintech',
      logistics: 'Lojistik ve Tedarik Zinciri',
      education: 'E\u011fitim ve \u00d6\u011fretim',
      media: 'Medya ve E\u011flence',
      agriculture: 'Tar\u0131m ve \u00c7ift\u00e7ilik',
      manufacturing: 'Helal \u00dcretim ve Ticaret',
      retail: 'Perakende ve E-ticaret',
      certification: 'Helal Sertifikasyon Kurulu\u015flar\u0131',
      government: 'Devlet ve Ticaret Ajanslar\u0131',
      investment: 'Yat\u0131r\u0131m ve Risk Sermayesi',
      others: 'Di\u011fer'
    }
  },
  id: {
    nav: {
      home: 'Beranda',
      about: 'Tentang',
      gallery: 'Galeri',
      packages: 'Paket',
      contact: 'Kontak',
      registerNow: 'Daftar Sekarang',
      destinations: 'Destinasi'
    },
    hero: {
      title: 'ASEAN Business Forum',
      subtitle: 'Platform Strategis untuk Perdagangan, Investasi & Ekonomi Halal',
      location: 'Batam & Bintan, Indonesia',
      ctaPrimary: 'Jadi Peserta Pameran',
      ctaSecondary: 'Unduh Brosur',
      year: '2026',
      feature: 'Pencocokan B2B'
    },
    countdown: {
      title: 'Hitung Mundur Acara',
      subtitle: 'Forum Dimulai',
      days: 'Hari',
      hours: 'Jam',
      minutes: 'Menit',
      seconds: 'Detik'
    },
    countdownFeatures: {
      b2b: 'Pencocokan B2B',
      b2bDesc: 'Pertemuan terkurasi dengan pembeli, pedagang & distributor berkualitas.',
      marketplace: 'Pameran & Perdagangan',
      marketplaceDesc: 'Ruang pameran khusus di sektor halal prioritas.',
      delegations: 'Pemerintah & Delegasi',
      delegationsDesc: 'B2G, G2G & jembatan global dengan delegasi resmi.',
      networking: 'Investasi & Jejaring',
      networkingDesc: 'Pertemuan investor utama & fasilitasi kemitraan strategis eksklusif.'
    },
    stats: {
      exhibitors: 'Peserta Pameran',
      countries: 'Negara',
      delegations: 'Delegasi',
      attendees: 'Peserta'
    },
    why: {
      title: 'Mengapa Menghadiri ASEAN Business Forum?',
      description: 'Ekonomi halal global telah berkembang melampaui konsumsi menjadi mesin strategis untuk perdagangan, investasi, dan pembangunan industri. Saat pasar ASEAN dan OKI memperdalam kerja sama ekonomi, forum ini menyediakan platform terpercaya untuk eksekusi ekonomi nyata.',
      marketGrowth: 'Peluang Triliunan Dolar',
      marketGrowthDesc: 'Akses ke ekonomi halal yang berkembang pesat memasuki fase eksekusi.',
      globalNetwork: 'Konektivitas Lintas Batas',
      globalNetworkDesc: 'Terhubung dengan pemangku kepentingan di pasar ASEAN, OKI, GCC, UE dan Asia.',
      innovationHub: 'Keunggulan Zona Perdagangan Bebas',
      innovationHubDesc: 'Manfaatkan keunggulan ganda Zona Perdagangan Bebas & Zona Ekonomi Khusus di Batam-Bintan.',
      credibility: 'Platform Berbasis Eksekusi',
      credibilityDesc: 'Forum berorientasi hasil yang berfokus pada fasilitasi perdagangan dan aktivasi investasi.'
    },
    about: {
      title: 'Tentang Forum',
      connect: 'Terhubung',
      connectDesc: 'Platform tingkat tinggi yang menghubungkan pemerintah, investor, institusi, dan pemimpin industri untuk mempercepat perdagangan dan kolaborasi lintas batas dalam ekonomi halal global.',
      showcase: 'Kolaborasi',
      showcaseDesc: 'Memfasilitasi kemitraan strategis, penandatanganan MoU, dan kerja sama publik-swasta di infrastruktur digital, logistik, manufaktur, pariwisata, dan industri halal.',
      scale: 'Berkembang',
      scaleDesc: 'Memungkinkan aliran kesepakatan jangka panjang, kontinuitas investasi, dan peningkatan skala industri di pasar ASEAN-OKI-Global dengan peluang Zona Perdagangan Bebas.'
    },
    destinations: {
      title: 'Destinasi Tuan Rumah: Batam & Bintan, INDONESIA (ZONA PERDAGANGAN BEBAS)',
      batam: 'BATAM',
      batamDesc: 'Gerbang perdagangan dan digital terhubung Singapura. Ekosistem industri, logistik, dan pelabuhan yang matang. Lingkungan pusat data dan infrastruktur digital termasuk Nongsa Digital Park dengan investasi lebih dari US$410 juta.',
      bintan: 'BINTAN',
      bintanDesc: 'Basis hemat biaya untuk investasi dan manufaktur. Lokasi strategis dekat Singapura. Potensi kuat di industri halal, pariwisata, dan kesehatan. Dibangun untuk investasi berkelanjutan jangka panjang.'
    },
    packages: {
      title: 'Paket Partisipasi',
      subtitle: 'Pilih tingkat keterlibatan yang tepat untuk organisasi Anda',
      delegate: 'Delegasi',
      delegateDesc: 'Untuk pemimpin pemerintah, investor, dan profesional industri yang mencari koneksi strategis.',
      booth: 'Peserta Pameran',
      boothDesc: 'Untuk perusahaan, kawasan, dan institusi yang ingin mempresentasikan solusi dan berinteraksi dengan pembeli.',
      sponsorship: 'Mitra Strategis',
      sponsorshipDesc: 'Visibilitas dan branding maksimal untuk organisasi yang membentuk ekonomi halal.',
      contactUs: 'Hubungi Kami',
      learnMore: 'Pelajari Lebih Lanjut',
      popular: 'Populer',
      features: {
        fullAccess: 'Akses penuh forum',
        networkingSessions: 'Sesi networking',
        conferenceMaterials: 'Materi konferensi',
        certificate: 'Sertifikat partisipasi',
        premiumSpace: 'Ruang pameran premium',
        b2bMatchmaking: 'Sesi pencocokan B2B',
        marketingInclusion: 'Termasuk pemasaran',
        leadGeneration: 'Alat generasi prospek',
        premiumBranding: 'Penempatan branding premium',
        speakingOpportunities: 'Kesempatan berbicara',
        vipAccess: 'Akses networking VIP',
        mediaCoverage: 'Liputan media'
      }
    },
    gallery: {
      title: 'Kisah Sukses Kami',
      description: 'Membangun di atas keberhasilan Asian Business Forum 2025 di Batam',
      photos: 'Foto',
      videos: 'Video',
      viewAll: 'Lihat Semua',
      noVideos: 'Belum ada video tersedia',
      videosComingSoon: 'Video akan segera ditambahkan'
    },
    success: {
      title: 'Hasil Utama',
      story1Title: 'Fasilitasi Perdagangan',
      story1Metric: 'Penandatanganan MoU',
      story1Quote: 'Fasilitasi kemitraan strategis dan MoU, mendukung kolaborasi dengan jalur perdagangan dan investasi di sektor prioritas.',
      story1Author: 'Asian Business Forum 2025',
      story2Title: 'Pemberdayaan Perempuan',
      story2Metric: '300+ Pengusaha',
      story2Quote: 'Memberdayakan pengusaha perempuan dari 10+ negara melalui keynote inspiratif, pencocokan bisnis, dan koneksi bermakna.',
      story2Author: 'Women Entrepreneurs Summit',
      story3Title: 'Kemitraan Pemerintah',
      story3Metric: 'Publik-Swasta',
      story3Quote: 'Koneksi bisnis lintas batas, keselarasan kebijakan, dan peningkatan visibilitas untuk kawasan dan institusi yang berpartisipasi.',
      story3Author: 'Kolaborasi ASEAN-OKI'
    },
    brochure: {
      title: 'Brosur Resmi Forum',
      description: 'Unduh brosur lengkap ASEAN Business Forum 2026 untuk agenda detail, daftar pembicara, informasi venue, dan peluang kemitraan.',
      download: 'Unduh Brosur (PDF)',
      requestDeck: 'Minta Deck Kemitraan',
      note: 'Termasuk paket peserta pameran, denah lantai, dan tingkat kemitraan strategis.',
      comingSoon: 'Brosur akan segera tersedia'
    },
    exhibitor: {
      title: 'Jadi Peserta Pameran',
      benefitsTitle: 'Keuntungan Peserta Pameran',
      globalVisibility: 'Eksposur Strategis',
      globalVisibilityDesc: 'Branding menonjol dalam audiens pengambil keputusan terkurasi tingkat tinggi.',
      curatedMeetings: 'Pertemuan B2B Terkurasi',
      curatedMeetingsDesc: 'Pertemuan terjadwal dengan pembeli, pedagang & distributor berkualitas.',
      accessDelegations: 'Akses Pemerintah',
      accessDelegationsDesc: 'Interaksi langsung dengan delegasi negara resmi dan pembuat kebijakan.',
      marketingInclusion: 'Eksposur Investasi',
      marketingInclusionDesc: 'Kunjungan lokasi di Bintan yang menyoroti peluang Zona Perdagangan Bebas.',
      networkingEvents: 'Acara Networking',
      networkingEventsDesc: 'Makan malam eksklusif, meja bundar, dan sesi pertemuan investor utama.'
    },
    form: {
      title: 'Minta Informasi',
      fullName: 'Nama Lengkap',
      company: 'Perusahaan / Organisasi',
      country: 'Negara',
      email: 'Email',
      whatsapp: 'WhatsApp',
      category: 'Industri / Sektor',
      selectCategory: 'Pilih sektor',
      interests: 'Saya tertarik dengan',
      delegate: 'Delegasi (Pengunjung)',
      booth: 'Peserta Pameran / Stan',
      sponsorship: 'Kemitraan Strategis',
      message: 'Pesan',
      submit: 'Kirim Pertanyaan',
      submitting: 'Mengirim...',
      successMessage: 'Terima kasih! Pertanyaan Anda telah dikirim. Tim kami akan menghubungi Anda dalam 48 jam.',
      errorMessage: 'Gagal mengirim formulir. Silakan coba lagi.',
      selectInterest: 'Silakan pilih minimal satu minat'
    },
    contact: {
      title: 'Hubungi Kami',
      subtitle: 'Hubungi tim kami',
      whatsapp: 'WhatsApp',
      email: 'Email',
      location: 'Lokasi',
      sendMessage: 'Kirim pesan',
      sendEmail: 'Kirim email',
      viewDestinations: 'Lihat destinasi',
      formTitle: 'Kirim Pesan',
      name: 'Nama',
      messagePlaceholder: 'Pesan Anda...',
      sendButton: 'Kirim Pesan',
      sending: 'Mengirim...',
      successMessage: 'Terima kasih atas pesan Anda. Kami akan segera merespons.',
      errorMessage: 'Gagal mengirim pesan. Silakan coba lagi.'
    },
    footer: {
      quickLinks: 'Tautan Cepat',
      followUs: 'Ikuti Kami',
      copyright: '\u00a9 2026 ASEAN Business Forum. Semua hak dilindungi.',
      privacyPolicy: 'Kebijakan Privasi',
      termsOfService: 'Syarat Layanan',
      tagline: 'ASEAN Business Forum 2026 - Batam & Bintan, Indonesia'
    },
    common: {
      loading: 'Memuat...',
      error: 'Kesalahan',
      success: 'Berhasil',
      cancel: 'Batal',
      save: 'Simpan',
      delete: 'Hapus',
      edit: 'Edit',
      add: 'Tambah',
      required: 'Wajib',
      close: 'Tutup',
      viewMore: 'Lihat Lebih',
      readMore: 'Baca Selengkapnya'
    },
    categories: {
      foodBeverage: 'Makanan & Minuman',
      cosmetics: 'Kosmetik & Perawatan Pribadi',
      pharma: 'Farmasi & Kesehatan',
      fashion: 'Fashion & Busana Muslim',
      finance: 'Keuangan Syariah & Investasi',
      tourism: 'Pariwisata, Kesehatan & Perhotelan',
      technology: 'Ekonomi Digital & Fintech',
      logistics: 'Logistik & Rantai Pasokan',
      education: 'Pendidikan & Pelatihan',
      media: 'Media & Hiburan',
      agriculture: 'Pertanian & Peternakan',
      manufacturing: 'Manufaktur & Perdagangan Halal',
      retail: 'Ritel & E-commerce',
      certification: 'Lembaga Sertifikasi Halal',
      government: 'Pemerintah & Agen Perdagangan',
      investment: 'Investasi & Modal Ventura',
      others: 'Lainnya'
    }
  }
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return `[${key}]`;
      }
    }
    if (value === undefined || value === null) return `[${key}]`;
    return value;
  }, [language]);

  const getBilingualValue = useCallback((field, defaultKey = null) => {
    if (!field) return defaultKey ? t(defaultKey) : '';
    if (typeof field === 'object' && (field.en || field.tr || field.id)) {
      const value = field[language];
      if (value) return value;
      if (defaultKey) return t(defaultKey);
      return '';
    }
    if (typeof field === 'string') {
      if (language === 'en') return field;
      if (defaultKey) return t(defaultKey);
    }
    return '';
  }, [language, t]);

  const changeLanguage = useCallback((lang) => {
    if (['en', 'tr', 'id'].includes(lang)) setLanguage(lang);
  }, []);

  const validateTranslation = useCallback((text) => {
    if (!text || typeof text !== 'string' || language === 'en') return null;
    return null;
  }, [language]);

  const contextValue = useMemo(() => ({
    language, setLanguage: changeLanguage, t, getBilingualValue, validateTranslation,
    translations: translations[language], allTranslations: translations
  }), [language, changeLanguage, t, getBilingualValue, validateTranslation]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};
